import { compact } from "lodash";
import { ERentioFormField } from "../../components/components/Form";
import { CONFIG } from "../../config/app.config";
import { getValue } from "../../redux/form/form.utils";
import { EContractIbanisationStatus } from "../../types/contract.types";
import { getLocalizedText } from "../../utils/i18n/i18n.utils";
import { formatCurrency, getAmountWithVat } from "../../utils/number.utils";
import { join } from "../../utils/string.utils";
import { ETypeSelect } from "./schema.createPayment.0";
import { ECreatePaymentMethod, EField, } from "./schema.createPayment.types";
import { getNavigationTitle, getRelatedContract, } from "./schema.createPayment.utils";
// The splits of a cost billed to several tenants at once. Each becomes its
// own payment order, collected on its own.
var getCostSplits = function (extraData) {
    var _a, _b;
    var costSplits = ((_a = extraData.prefill) === null || _a === void 0 ? void 0 : _a.costPayment)
        ? (_b = extraData.prefill.costSplits) !== null && _b !== void 0 ? _b : []
        : [];
    return costSplits.length > 1 ? costSplits : [];
};
// The VAT percentage of the form's price; a "no VAT" reason counts as 0%.
var toVatPercentage = function (vat) {
    var percentage = Number(vat);
    return isNaN(percentage) ? 0 : percentage;
};
var isTooLargeForSepa = function (params) {
    return getAmountWithVat(params.amount, params.vat / 100) >=
        CONFIG.PAYMENT_LIMIT_FOR_SEPA;
};
// The amount (excl. VAT) the SEPA limit is checked against. A cost split
// over several tenants is one SEPA collection per tenant, so the limit
// applies to the largest split, never to the total of all tenants.
export var getSepaCheckAmount = function (params) {
    var price = params.price, extraData = params.extraData;
    var costSplits = getCostSplits(extraData);
    return costSplits.length > 0
        ? Math.max.apply(Math, costSplits.map(function (split) { return split.amount; })) : price;
};
export var isPriceTooLargeForSepa = function (params) {
    return isTooLargeForSepa({
        amount: getSepaCheckAmount(params),
        vat: toVatPercentage(params.vat),
    });
};
// The warning when SEPA is not possible. Split over tenants it names the
// tenants whose own payment is too large, when their names are known.
export var getTooLargeForSepaText = function (params) {
    var vat = params.vat, extraData = params.extraData;
    var amount = formatCurrency(CONFIG.PAYMENT_LIMIT_FOR_SEPA);
    var costSplits = getCostSplits(extraData);
    if (costSplits.length === 0) {
        return getLocalizedText("payments.create.methods_if_too_large", {
            amount: amount,
        });
    }
    var tooLarge = costSplits.filter(function (split) {
        return isTooLargeForSepa({ amount: split.amount, vat: toVatPercentage(vat) });
    });
    var payerNames = compact(tooLarge.map(function (split) { return split.payerName; }));
    if (payerNames.length > 0 && payerNames.length === tooLarge.length) {
        return getLocalizedText("payments.create.methods_if_too_large.split_named", {
            amount: amount,
            payers: join(payerNames),
        });
    }
    return getLocalizedText("payments.create.methods_if_too_large.split", {
        amount: amount,
    });
};
export var getSchema = function (initialValues, extraData) {
    return function (defaultValues) {
        var _a;
        return ({
            required: [EField.PaymentMethods],
            properties: (_a = {},
                _a[EField.Header + "4b"] = {
                    default: undefined,
                },
                _a[EField.PaymentMethodsTooLargeInfo] = {
                    // @ts-ignore
                    showIf: function (formData) {
                        return isPriceTooLargeForSepa({
                            price: formData[EField.Price] || 0,
                            vat: formData[EField.PriceVat],
                            extraData: extraData,
                        });
                    },
                },
                _a[EField.PaymentMethods] = {
                    default: (function () {
                        if (!defaultValues[EField.TypeSelect] && !extraData.paymentOrder) {
                            return undefined;
                        }
                        if (!extraData.paymentOrder) {
                            var isRecurring = defaultValues[EField.TypeSelect] === ETypeSelect.Recurring;
                            var result = defaultValues[EField.PaymentMethods] ||
                                compact([
                                    isRecurring ? ECreatePaymentMethod.SepaRecurring : undefined,
                                    ECreatePaymentMethod.Online,
                                ]);
                            var contract = getRelatedContract(extraData, defaultValues);
                            var ibanisationStatus = (contract === null || contract === void 0 ? void 0 : contract.ibanisationStatus) || EContractIbanisationStatus.None;
                            if ([
                                EContractIbanisationStatus.Enabling,
                                EContractIbanisationStatus.Enabled,
                            ].includes(ibanisationStatus) &&
                                !result.includes(ECreatePaymentMethod.Ibanisation)) {
                                result.push(ECreatePaymentMethod.Ibanisation);
                            }
                            var price = getValue(EField.Price, initialValues, defaultValues) || 0;
                            var filledVatValue = getValue(EField.PriceVat, initialValues, defaultValues);
                            if (isPriceTooLargeForSepa({
                                price: price,
                                vat: filledVatValue,
                                extraData: extraData,
                            })) {
                                result = result.filter(function (item) {
                                    return item !== ECreatePaymentMethod.SepaOnce &&
                                        item !== ECreatePaymentMethod.SepaRecurring;
                                });
                            }
                            return result;
                        }
                        return getValue(EField.PaymentMethods, initialValues, defaultValues);
                    })(),
                    // @ts-ignore
                    options: (function () {
                        var isRecurring = defaultValues[EField.TypeSelect] === ETypeSelect.Recurring;
                        return Object.values(ECreatePaymentMethod)
                            .filter(function (paymentMethod) {
                            if (paymentMethod === ECreatePaymentMethod.Ibanisation) {
                                return extraData.ibanisationEnabled || false;
                            }
                            if (paymentMethod === ECreatePaymentMethod.SepaRecurring) {
                                return isRecurring;
                            }
                            return true;
                        })
                            .map(function (paymentMethod) { return ({
                            id: paymentMethod,
                            disabled: (function () {
                                if (paymentMethod === ECreatePaymentMethod.Ibanisation) {
                                    return true;
                                }
                                // Disable SEPA for amounts greater than 3_500 (CONFIG.PAYMENT_LIMIT_FOR_SEPA)
                                if (paymentMethod === ECreatePaymentMethod.SepaOnce ||
                                    paymentMethod === ECreatePaymentMethod.SepaRecurring) {
                                    var price = getValue(EField.Price, initialValues, defaultValues) || 0;
                                    var filledVatValue = getValue(EField.PriceVat, initialValues, defaultValues);
                                    if (isPriceTooLargeForSepa({
                                        price: price,
                                        vat: filledVatValue,
                                        extraData: extraData,
                                    })) {
                                        return true;
                                    }
                                }
                                return false;
                            })(),
                            value: getLocalizedText("payments.create.payment_methods.option.label.".concat(paymentMethod)),
                            subValue: getLocalizedText("payments.create.payment_methods.option.info.".concat(paymentMethod)),
                        }); });
                    })(),
                },
                _a[EField.PaymentMethodsIbanisationInfo] = {
                    // @ts-ignore
                    showIf: function (formData) {
                        return extraData.ibanisationEnabled &&
                            !!getRelatedContract(extraData, formData);
                    },
                },
                _a),
        });
    };
};
export var uiSchema = function (extraData) {
    var _a;
    return _a = {
            "rentio:title": getLocalizedText("payments.create.payment_methods.heading.summary"),
            "rentio:navigationTitle": getNavigationTitle(extraData)
        },
        _a[EField.Header + "4b"] = {
            "ui:field": ERentioFormField.SectionHeader,
            title: getLocalizedText("payments.create.payment_methods.heading"),
        },
        _a[EField.PaymentMethodsTooLargeInfo] = {
            "ui:field": ERentioFormField.HelpInfo,
            type: "warning",
            getSubtitle: function (_, formData) {
                return getTooLargeForSepaText({
                    vat: formData === null || formData === void 0 ? void 0 : formData[EField.PriceVat],
                    extraData: extraData,
                });
            },
        },
        _a[EField.PaymentMethods] = {
            "ui:field": ERentioFormField.InlineSelect,
            summaryTitle: getLocalizedText("payments.create.payment_methods.option.title.summary"),
            formatSummary: function (formData) {
                return formData && formData.length > 0
                    ? join(formData.map(function (item) {
                        return getLocalizedText("payments.create.payment_methods.option.label.".concat(item));
                    }))
                    : undefined;
            },
            isMultiSelect: true,
        },
        _a[EField.PaymentMethodsIbanisationInfo] = {
            "ui:field": ERentioFormField.HelpInfo,
            getSubtitle: function (_, formData) {
                var contract = getRelatedContract(extraData, formData);
                if (!contract) {
                    return "";
                }
                var ibanisationStatus = contract.ibanisationStatus || EContractIbanisationStatus.None;
                if (ibanisationStatus === EContractIbanisationStatus.None) {
                    return getLocalizedText("payments.create.ibanisation.info.disabled");
                }
                else {
                    return getLocalizedText("payments.create.ibanisation.info.enabled");
                }
            },
        },
        _a;
};
